/**
 * ── Billing Resolution Hook ──
 * Unified query point for answering:
 * - Who is paying for this entity?
 * - What plan are they on?
 * - Who owns the subscription?
 * - What does the payment unlock?
 * - Is this org billed directly or via an association?
 * - What capabilities should be enabled based on billing state?
 *
 * This is the primary hook for understanding billing ownership and entitlement
 * across the platform → association → organization hierarchy.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BillingAgreement, CommerceProduct, CommerceEntitlement } from "@/hooks/useCommerce";

// ── Types ────────────────────────────────────────────────────────────────────

export type BillingOwnerType = "platform" | "association" | "organization";

export interface BillingSource {
  /** The billing agreement driving this subscription */
  agreement: BillingAgreement & { commerce_products: CommerceProduct | null };
  /** Who provides the subscription */
  providerType: BillingOwnerType;
  providerId: string;
  /** Who pays */
  payerType: string;
  payerId: string;
  /** Who benefits */
  beneficiaryType: string;
  beneficiaryId: string;
}

export interface ResolvedEntitlement {
  id: string;
  type: string; // access, seats, feature, quota
  productName: string;
  status: string;
  grantedQuantity: number;
  usedQuantity: number;
  remainingQuantity: number;
  featureKey?: string;
  quotaType?: string;
  source: BillingOwnerType;
  agreementId: string;
  expiresAt?: string;
}

export interface BillingResolution {
  /** Primary plan name */
  planName: string | null;
  /** Whether the entity has any active billing */
  hasActiveBilling: boolean;
  /** Is this billed directly or via a parent? */
  billingModel: "direct_platform" | "direct_org" | "via_association" | "mixed" | "none";
  /** All active billing sources ordered by priority (platform > association > org) */
  sources: BillingSource[];
  /** All active entitlements from all sources, deduplicated */
  entitlements: ResolvedEntitlement[];
  /** Feature keys this entity has access to */
  activeFeatures: Set<string>;
  /** Quota summaries keyed by quota_type */
  quotas: Record<string, { granted: number; used: number; remaining: number }>;
  /** Total seats granted / used */
  seats: { granted: number; used: number; remaining: number } | null;
  /** Whether the entity is past due on any agreement */
  hasPastDue: boolean;
  /** Whether any agreement auto-renews */
  hasAutoRenew: boolean;
  /** Earliest expiration among active agreements */
  nextExpiration: string | null;
  /** Total monthly cost (estimated across all sources) */
  estimatedMonthlyCost: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeToMonthly(price: number, frequency: string | null): number {
  switch (frequency) {
    case "annual":
      return price / 12;
    case "quarterly":
      return price / 3;
    case "one_time":
      return 0;
    default:
      return price;
  }
}

function resolveEntitlements(
  raw: (CommerceEntitlement & {
    commerce_products: CommerceProduct | null;
    billing_agreements: BillingAgreement | null;
  })[],
  providerType: BillingOwnerType,
): ResolvedEntitlement[] {
  return raw
    .filter((e) => e.status === "active")
    .map((e) => {
      const meta = (e.metadata_json as Record<string, unknown>) || {};
      const granted = e.granted_quantity || 0;
      const used = e.used_quantity || 0;
      return {
        id: e.id,
        type: e.entitlement_type,
        productName: e.commerce_products?.name || "Unknown",
        status: e.status,
        grantedQuantity: granted,
        usedQuantity: used,
        remainingQuantity: Math.max(0, granted - used),
        featureKey: meta.feature_key as string | undefined,
        quotaType: meta.quota_type as string | undefined,
        source: providerType,
        agreementId: e.billing_agreement_id,
        expiresAt: e.expires_at || undefined,
      };
    });
}

// ── Hook: Resolve Entity Billing ─────────────────────────────────────────────

/**
 * Resolves the complete billing picture for an entity (org or association).
 * Aggregates billing from all sources: platform-provided, association-provided,
 * and self-owned subscriptions.
 */
export function useEntityBillingResolution(entityId: string, entityType: "organization" | "association") {
  // 1. Platform-provided agreements for this entity
  const { data: platformAgreements = [], isLoading: loadingPlatform } = useQuery({
    queryKey: ["billing_resolution_platform", entityId, entityType],
    enabled: !!entityId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_agreements")
        .select("*, commerce_products(*)")
        .eq("beneficiary_id", entityId)
        .eq("beneficiary_type", entityType)
        .eq("provider_type", "platform")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as (BillingAgreement & { commerce_products: CommerceProduct })[];
    },
  });

  // 2. Association-provided agreements (only for orgs)
  const { data: associationAgreements = [], isLoading: loadingAssoc } = useQuery({
    queryKey: ["billing_resolution_association", entityId],
    enabled: !!entityId && entityType === "organization",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_agreements")
        .select("*, commerce_products(*)")
        .eq("beneficiary_id", entityId)
        .eq("beneficiary_type", "organization")
        .eq("provider_type", "association")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as (BillingAgreement & { commerce_products: CommerceProduct })[];
    },
  });

  // 3. Self-purchased agreements (org buying add-ons directly)
  const { data: selfAgreements = [], isLoading: loadingSelf } = useQuery({
    queryKey: ["billing_resolution_self", entityId],
    enabled: !!entityId && entityType === "organization",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_agreements")
        .select("*, commerce_products(*)")
        .eq("payer_id", entityId)
        .eq("payer_type", "organization")
        .eq("provider_type", "organization")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as (BillingAgreement & { commerce_products: CommerceProduct })[];
    },
  });

  // 4. All entitlements for this entity
  const { data: entitlements = [], isLoading: loadingEnts } = useQuery({
    queryKey: ["billing_resolution_entitlements", entityId, entityType],
    enabled: !!entityId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("commerce_entitlements")
        .select("*, commerce_products(*), billing_agreements(*)")
        .eq("beneficiary_id", entityId)
        .eq("beneficiary_type", entityType)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as (CommerceEntitlement & {
        commerce_products: CommerceProduct | null;
        billing_agreements: BillingAgreement | null;
      })[];
    },
  });

  const isLoading = loadingPlatform || loadingAssoc || loadingSelf || loadingEnts;

  // ── Resolution ──
  const resolution = useMemo((): BillingResolution => {
    // Build sources
    const sources: BillingSource[] = [];

    const addSource = (
      a: BillingAgreement & { commerce_products: CommerceProduct | null },
      providerType: BillingOwnerType,
    ) => {
      sources.push({
        agreement: a,
        providerType,
        providerId: a.provider_id,
        payerType: a.payer_type,
        payerId: a.payer_id,
        beneficiaryType: a.beneficiary_type,
        beneficiaryId: a.beneficiary_id,
      });
    };

    platformAgreements.forEach((a) => {
      addSource(a, "platform");
    });
    associationAgreements.forEach((a) => {
      addSource(a, "association");
    });
    selfAgreements.forEach((a) => {
      addSource(a, "organization");
    });

    const activeSources = sources.filter((s) => s.agreement.status === "active");

    // Build agreement ID → provider type map for entitlement resolution
    const agreementProviderMap: Record<string, BillingOwnerType> = {};
    sources.forEach((s) => {
      agreementProviderMap[s.agreement.id] = s.providerType;
    });

    // Resolve entitlements
    const resolvedEnts = entitlements
      .filter((e) => e.status === "active")
      .map((e) => {
        const meta = (e.metadata_json as Record<string, unknown>) || {};
        const granted = e.granted_quantity || 0;
        const used = e.used_quantity || 0;
        const source = agreementProviderMap[e.billing_agreement_id] || "organization";
        return {
          id: e.id,
          type: e.entitlement_type,
          productName: e.commerce_products?.name || "Unknown",
          status: e.status,
          grantedQuantity: granted,
          usedQuantity: used,
          remainingQuantity: Math.max(0, granted - used),
          featureKey: meta.feature_key as string | undefined,
          quotaType: meta.quota_type as string | undefined,
          source,
          agreementId: e.billing_agreement_id,
          expiresAt: e.expires_at || undefined,
        } satisfies ResolvedEntitlement;
      });

    // Feature set
    const activeFeatures = new Set<string>();
    resolvedEnts.forEach((e) => {
      if (e.type === "feature" && e.featureKey) activeFeatures.add(e.featureKey);
    });

    // Quotas
    const quotas: Record<string, { granted: number; used: number; remaining: number }> = {};
    resolvedEnts.forEach((e) => {
      if (e.type === "quota" && e.quotaType) {
        const existing = quotas[e.quotaType] || { granted: 0, used: 0, remaining: 0 };
        existing.granted += e.grantedQuantity;
        existing.used += e.usedQuantity;
        existing.remaining += e.remainingQuantity;
        quotas[e.quotaType] = existing;
      }
    });

    // Seats
    const seatEnts = resolvedEnts.filter((e) => e.type === "seats");
    const seats =
      seatEnts.length > 0
        ? {
            granted: seatEnts.reduce((s, e) => s + e.grantedQuantity, 0),
            used: seatEnts.reduce((s, e) => s + e.usedQuantity, 0),
            remaining: seatEnts.reduce((s, e) => s + e.remainingQuantity, 0),
          }
        : null;

    // Billing model
    const hasPlatform = activeSources.some((s) => s.providerType === "platform");
    const hasAssociation = activeSources.some((s) => s.providerType === "association");
    const hasOrg = activeSources.some((s) => s.providerType === "organization");

    let billingModel: BillingResolution["billingModel"] = "none";
    if (activeSources.length === 0) billingModel = "none";
    else if (hasPlatform && !hasAssociation && !hasOrg) billingModel = "direct_platform";
    else if (hasAssociation && !hasPlatform && !hasOrg) billingModel = "via_association";
    else if (hasOrg && !hasPlatform && !hasAssociation) billingModel = "direct_org";
    else billingModel = "mixed";

    // Plan name — use highest-priority active agreement's product name
    const primarySource =
      activeSources.find((s) => s.providerType === "platform") ||
      activeSources.find((s) => s.providerType === "association") ||
      activeSources[0];
    const planName = primarySource?.agreement.commerce_products?.name || null;

    // Flags
    const hasPastDue = sources.some((s) => s.agreement.status === "past_due");
    const hasAutoRenew = activeSources.some((s) => s.agreement.auto_renew);

    // Next expiration
    const expirations = activeSources
      .filter((s) => s.agreement.end_date)
      .map((s) => s.agreement.end_date!)
      .sort();
    const nextExpiration = expirations[0] || null;

    // Estimated monthly cost
    const estimatedMonthlyCost = activeSources.reduce(
      (sum, s) => sum + normalizeToMonthly(s.agreement.total_price, s.agreement.billing_frequency),
      0,
    );

    return {
      planName,
      hasActiveBilling: activeSources.length > 0,
      billingModel,
      sources,
      entitlements: resolvedEnts,
      activeFeatures,
      quotas,
      seats,
      hasPastDue,
      hasAutoRenew,
      nextExpiration,
      estimatedMonthlyCost,
    };
  }, [platformAgreements, associationAgreements, selfAgreements, entitlements]);

  return { ...resolution, isLoading };
}

// ── Hook: Quick Billing Feature Check ────────────────────────────────────────

/**
 * Lightweight hook that checks if an entity has a specific feature entitlement
 * from any billing source.
 */
export function useBillingFeatureCheck(
  entityId: string,
  entityType: "organization" | "association",
  featureKey: string,
) {
  const { activeFeatures, isLoading } = useEntityBillingResolution(entityId, entityType);
  return { hasFeature: activeFeatures.has(featureKey), isLoading };
}

// ── Hook: Billing Ownership Summary ──────────────────────────────────────────

/**
 * Returns a human-readable billing ownership summary for an entity.
 * Useful for displaying "Billed via TCCC Association" or "Direct Platform Plan: Pro"
 */
export function useBillingOwnershipLabel(entityId: string, entityType: "organization" | "association") {
  const { billingModel, planName, sources, isLoading } = useEntityBillingResolution(entityId, entityType);

  const label = useMemo(() => {
    if (!planName && sources.length === 0) return "No active plan";
    const activeSources = sources.filter((s) => s.agreement.status === "active");

    switch (billingModel) {
      case "direct_platform":
        return `Platform Plan: ${planName || "Active"}`;
      case "via_association":
        return `Via Association: ${planName || "Active"}`;
      case "direct_org":
        return `Self-managed: ${planName || "Active"}`;
      case "mixed": {
        const types = [...new Set(activeSources.map((s) => s.providerType))];
        return `Mixed billing (${types.join(", ")})`;
      }
      default:
        return "No active plan";
    }
  }, [billingModel, planName, sources]);

  return { label, billingModel, isLoading };
}
